/**
 * Turning notifications on in this browser. F-102.
 *
 * Everything here is about *this device*. The server stores one row per browser, so the
 * question "are notifications on" has a different answer on a phone and on a laptop, and
 * a screen that asked the server would get one answer for both — which reads as the
 * switch being stuck.
 */
import { getPushState, subscribePush, unsubscribePush, isError } from './api';

/** Where the service worker is served from. It must be at the root; see `sw.js`. */
const WORKER_URL = '/sw.js';

export type PushSupport =
  /** Everything needed is present. */
  | 'ready'
  /** The browser has no Web Push at all — an old Safari, a privacy build, a webview. */
  | 'unsupported'
  /**
   * iOS, in a tab. Push exists from 16.4 but **only** for a site added to the home
   * screen, and `Notification` is not even defined until then — so this is not "denied",
   * it is "not yet installed", and the screen has to say so or the learner taps a button
   * that does nothing.
   */
  | 'needs_install';

/**
 * Whether the page is running as an installed app rather than in a browser tab.
 *
 * Two checks because the two platforms answer differently: `display-mode: standalone` is
 * the standard media query and is what Android's WebAPK matches, and `navigator.standalone`
 * is the older non-standard property that iOS still uses for a home-screen shortcut.
 * Checking only the first reports every iOS install as a tab, which would leave every
 * iPhone learner staring at install instructions they have already followed.
 */
export function installed(): boolean {
  const legacy = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return legacy || window.matchMedia('(display-mode: standalone)').matches;
}

/**
 * iOS and iPadOS, including the iPad that reports itself as a Mac.
 *
 * User-agent sniffing, which is normally the wrong tool and is the right one here: the
 * thing being detected is a *platform policy* — that notifications require a home-screen
 * install — and there is no feature to test for it. `PushManager` is simply absent in an
 * iOS tab, which is indistinguishable from a browser that has no push at all, and the two
 * want completely different copy: one is "add this to your home screen and it will work",
 * the other is "this browser cannot do it".
 *
 * iPadOS 13+ reports `MacIntel`, so the touch-point count is what separates an iPad from a
 * desktop Safari. Both would be told the same thing anyway; the check is here so a desktop
 * Mac is not told to add anything to a home screen it does not have.
 */
export function isApple(): boolean {
  if (/iPad|iPhone|iPod/.test(navigator.userAgent)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

export function support(): PushSupport {
  if ('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window) {
    return 'ready';
  }
  if (isApple() && !installed()) return 'needs_install';
  return 'unsupported';
}

/**
 * The VAPID public key as `pushManager.subscribe` wants it: raw bytes.
 *
 * **The base64url string cannot be passed straight through.** The specification allows a
 * string, Chrome accepts one, and Safari and Firefox do not — they throw
 * `InvalidCharacterError` or simply reject, and the failure appears only on the platforms
 * a Mac-less developer cannot easily test. Converting here means every browser gets the
 * form all of them accept.
 *
 * `atob` needs standard base64, and a VAPID key is base64**url** with no padding: `-` and
 * `_` have to come back to `+` and `/`, and the padding has to be restored, or the decode
 * silently produces the wrong bytes and the subscription is created against a key nothing
 * can sign for.
 */
function applicationServerKey(base64url: string): Uint8Array {
  const padded = base64url.padEnd(base64url.length + ((4 - (base64url.length % 4)) % 4), '=');
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export type EnableResult =
  | { ok: true; devices: number }
  | { ok: false; reason: 'denied' | 'not_configured' | 'failed' };

/**
 * Asks for permission, subscribes, and tells the server.
 *
 * **`Notification.requestPermission()` is called first, before any `await`.** That is the
 * whole reason the order in this function looks odd. Safari only honours a permission
 * request that is still inside the user gesture that triggered it, and an `await` — for
 * the service worker registration, for the public key — ends the gesture: the prompt is
 * then refused without being shown, which the page sees as a plain `denied` and reports to
 * the learner as "you said no" to a dialog nobody was offered.
 *
 * So: permission, then everything else. It must also be called from a click handler rather
 * than on page load, which is the caller's job and is why there is no `useEffect` doing
 * this anywhere.
 */
export async function enablePush(): Promise<EnableResult> {
  let permission: NotificationPermission;
  try {
    permission = await Notification.requestPermission();
  } catch {
    return { ok: false, reason: 'failed' };
  }
  if (permission !== 'granted') return { ok: false, reason: 'denied' };

  try {
    const state = await getPushState();
    if (isError(state) || !state.configured || !state.publicKey) {
      return { ok: false, reason: 'not_configured' };
    }

    const registration = await navigator.serviceWorker.register(WORKER_URL, { scope: '/' });
    // `register` resolves as soon as the worker is registered, which can be before it is
    // active — and `pushManager.subscribe` on an installing worker fails. `ready` is the
    // promise that waits for an active one.
    await navigator.serviceWorker.ready;

    /*
     * Reuse an existing subscription rather than creating a second one.
     *
     * `subscribe` is idempotent for the same key, but calling it while a subscription
     * exists for a *different* key throws `InvalidStateError` — which is what a learner
     * whose keys were rotated would hit, once, for ever. Taking the existing one when
     * there is one keeps this function safe to call on every visit.
     */
    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        // Required by every browser, and it is a promise rather than a setting: every
        // push this subscription receives must produce a visible notification. `sw.js`
        // keeps it even for a payload it cannot parse.
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey(state.publicKey),
      }));

    const json = subscription.toJSON();
    const saved = await subscribePush({
      endpoint: subscription.endpoint,
      keys: { p256dh: json.keys?.['p256dh'] ?? '', auth: json.keys?.['auth'] ?? '' },
      // The language this browser is showing, not the learner's stored locale. The banner
      // is written for whoever is holding this device.
      locale: document.documentElement.lang || 'es',
    });
    if (isError(saved)) return { ok: false, reason: 'failed' };

    return { ok: true, devices: saved.devices };
  } catch {
    return { ok: false, reason: 'failed' };
  }
}

/**
 * Turns notifications off for this browser, on both sides.
 *
 * The browser's subscription is dropped *and* the server's row is deleted, and the order
 * matters less than doing both: a row left behind is a device the dispatcher keeps trying
 * until a push service returns 410, and a subscription left in the browser makes a later
 * "turn it on" reuse an endpoint the server has forgotten.
 *
 * The server is told even when the browser has nothing to unsubscribe. That is the case
 * where somebody revoked permission in browser settings — the subscription is already
 * gone locally and the row is still there, which is exactly the state that needs clearing.
 */
export async function disablePush(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.getRegistration('/');
    const subscription = await registration?.pushManager.getSubscription();
    if (subscription) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      await unsubscribePush(endpoint);
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Whether this browser currently holds a subscription.
 *
 * Asked of the browser rather than of the server, because the server knows how many
 * devices the learner has registered and not which one you are reading this on.
 */
export async function subscribedHere(): Promise<boolean> {
  try {
    if (support() !== 'ready') return false;
    const registration = await navigator.serviceWorker.getRegistration('/');
    return Boolean(await registration?.pushManager.getSubscription());
  } catch {
    return false;
  }
}

/** What the browser last decided about notifications, without asking again. */
export function permission(): NotificationPermission | 'unavailable' {
  return 'Notification' in window ? Notification.permission : 'unavailable';
}
